// Sitting down (椅子、长凳、工位、会议圆桌): the map's seats (public/seats.js), the office's word on who sits where
// (src/office/seats.ts). Walk up to a seat and press E (or click / tap the seat: you walk there and sit on arrival);
// E again, or walking off, stands you up. Near a free seat, the map shows a soft ring at its foot and a key hint
// (deck/seats-view.js). The office puts you on the spot facing the seat's way and tells everyone ("seat"), so the
// others (and anyone arriving later) see you sitting: at a desk typing (工作中), at a meeting table in its voice circle.
import { SEAT_REACH, SEATED, seatById, seatDist, nearestSeat, seatAtPoint, sittingOn, seatFacing, seatSpot } from '/seats.js';
import { t, errorText } from '/i18n.js';

const TOUCH = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

/**
 * @param {object} o
 * @param {object} o.MAP the map (deck/map.js; MAP.seats)
 * @param {() => object|null} o.me  @param {() => Map} o.peers  @param {(m: object) => void} o.send
 * @param {object} o.mover deck/move.js  @param {(text: string) => void} o.toast
 * @param {() => Array} [o.gameTables] the board-game tables with how many sit there (public/gomoku.js tables())
 */
export function initSit({ MAP, me, peers, send, mover, toast, gameTables = () => [] }) {
  // the board-game tables' stools (public/seats.js seatSpot, the office's own chair spots), as seats for the sit pose:
  // a player there faces the board (the office keeps them turned to it), so whoever is on a stool of a table with
  // someone seated, facing its way, is drawn sitting. Nothing new on the wire; not E / tap seats (gomoku.js has those).
  const stools = (MAP.tables ?? []).flatMap((t) => [1, 2].map((c) => {
    const s = seatSpot(MAP, t, c);
    return s && { id: `gm-${t.id}-${c}`, table: t.id, kind: 'stool', group: null, lift: 0, dir16: s.dir16, x: s.x, y: s.y, fx: s.x + 0.5, fy: s.y + 0.8 };
  })).filter(Boolean);
  const onStool = (p) => {
    if (p.boat || !stools.length) return null;
    const s = stools.find((q) => seatDist(q, p.x, p.y) <= 0.35 && (p.dir16 ?? (p.dir & 3) * 4) === q.dir16);
    return s && gameTables().some((t) => t.id === s.table && t.seated > 0) ? s : null;
  };
  let pending = null; // {id, until, tried}: walking to a seat, to sit on arrival
  /** Someone other than me sits on seat `id`. */
  const taken = (id) => { for (const p of peers().values()) if (p.seat === id && sittingOn(MAP, p)) return true; return false; };
  const mySeat = () => sittingOn(MAP, me());

  function sit(s) { pending = { id: s.id, until: Date.now() + 15000, tried: Date.now() }; send({ t: 'sit', seat: s.id }); }
  /** E: sit on the nearest free seat in reach, or stand up if I'm sitting. False if there's nothing to do here. */
  function interact() {
    const m = me(); if (!m || m.boat) return false;
    if (mySeat()) { send({ t: 'stand' }); return true; }
    const s = nearestSeat(MAP, m.x, m.y, taken);
    if (!s) return false;
    sit(s); return true;
  }
  /** A click / tap on the map at (wx, wy) tiles: on a seat, walk there and sit (true: handled). */
  function seatClick(wx, wy) {
    const m = me(), s = m && seatAtPoint(MAP, wx, wy); if (!s) return false;
    return goSit(s);
  }
  /** Walk up to seat s and sit down on arrival (public/pick.js 坐下; a double click on it). False: someone sits there. */
  function goSit(s) {
    const m = me(); if (!m) return false;
    if (mySeat()?.id === s.id) return true;
    if (taken(s.id)) return false; // someone sits there: a click on them is as before (walk there; their card)
    if (!m.boat && seatDist(s, m.x, m.y) <= SEAT_REACH) { sit(s); return true; }
    pending = { id: s.id, until: Date.now() + 60000, tried: 0 };
    const request = pending;
    void mover.travelTo(Math.floor(s.x + 0.5), Math.floor(s.y + 0.8), true, [s.x, s.y]).then((started) => {
      if (!started && pending === request) pending = null;
    });
    return true;
  }
  // arrived: sit (again each second until the office has me on it: my last step may reach it after this)
  setInterval(() => {
    const m = me();
    if (!pending) return;
    if (!m || Date.now() > pending.until || mover.keys?.size) { pending = null; return; }
    const s = seatById(MAP, pending.id);
    if (!s || (m.seat === s.id && mySeat())) { pending = null; return; }
    const there = seatDist(s, m.x, m.y);
    if (!m.boat && !mover.planning() && there <= SEAT_REACH && (!m.moving || there <= SEATED) && Date.now() - pending.tried > 1000) { pending.tried = Date.now(); send({ t: 'sit', seat: s.id }); }
  }, 200);

  function msg(m) {
    if (m.t === 'seat') {
      const self = me(), p = m.id === self?.id ? self : peers().get(m.id); if (!p) return;
      p.seat = m.seat ?? null;
      if (m.seat) {
        const s = seatById(MAP, m.seat);
        if (s) Object.assign(p, seatFacing(s));
        if (p === self) pending = null;
      }
    } else if (m.t === 'seat_err') {
      if (pending && (m.code === 'E_NOT_NEAR' || /走近/.test(m.reason))) return; // still on the way
      pending = null; toast(m.reason || m.code ? errorText('sit.err', { code: m.code, message: m.reason, params: m.params }) : t('desks.sit.failed')); // sit.err.<code>
    }
  }
  /** For the map (deck/seats-view.js): the seat to hint at (the free one I'd sit on with E), whether this is a touch
   *  screen, the seat I sit on. */
  function hint() {
    const m = me(); if (!m || m.boat) return null;
    const mine = mySeat();
    if (mine) return { seat: null, mine, touch: TOUCH };
    const s = nearestSeat(MAP, m.x, m.y, taken);
    return { seat: s, mine: null, touch: TOUCH };
  }
  return { interact, seatClick, goSit, stand: () => { if (mySeat()) send({ t: 'stand' }); }, msg, hint, seatOf: (p) => sittingOn(MAP, p) ?? onStool(p) };
}
